/**
 * React Error Boundary with Sentry Integration
 *
 * Catches React component errors and reports them to Sentry.
 * Provides a fallback UI when errors occur.
 *
 * @fileoverview Error boundary for React component tree
 */

import * as Sentry from '@sentry/react';
import { Component, type ErrorInfo, type ReactNode } from 'react';

interface Props {
  children: ReactNode;
  fallback?: ReactNode;
}

interface State {
  hasError: boolean;
  error: Error | null;
}

/**
 * Error Boundary Component
 *
 * Wraps the application or specific features to catch and handle errors.
 *
 * @example
 * <ErrorBoundary>
 *   <App />
 * </ErrorBoundary>
 *
 * @example
 * <ErrorBoundary fallback={<CustomErrorPage />}>
 *   <CriticalFeature />
 * </ErrorBoundary>
 */
export class ErrorBoundary extends Component<Props, State> {
  constructor(props: Props) {
    super(props);
    this.state = {
      hasError: false,
      error: null,
    };
  }

  static getDerivedStateFromError(error: Error): State {
    // Update state so the next render will show the fallback UI
    return {
      hasError: true,
      error,
    };
  }

  componentDidCatch(error: Error, errorInfo: ErrorInfo): void {
    // Log error to console in development
    if (import.meta.env.MODE === 'development') {
      console.error('Error caught by boundary:', error, errorInfo);
    }

    // Capture error in Sentry
    Sentry.withScope((scope) => {
      // Add React component stack trace
      scope.setContext('react', {
        componentStack: errorInfo.componentStack,
      });

      // Add tags
      scope.setTag('error_boundary', 'true');
      scope.setTag('error.type', 'react_error');

      // Capture exception
      Sentry.captureException(error);
    });
  }

  render(): ReactNode {
    if (this.state.hasError) {
      // Render custom fallback UI or default error message
      if (this.props.fallback) {
        return this.props.fallback;
      }

      return (
        <div className="flex min-h-screen items-center justify-center bg-gray-50 px-4 py-12 sm:px-6 lg:px-8">
          <div className="w-full max-w-md space-y-8">
            <div>
              <h2 className="mt-6 text-center text-3xl font-bold tracking-tight text-gray-900">
                Something went wrong
              </h2>
              <p className="mt-2 text-center text-sm text-gray-600">
                We're sorry, but something unexpected happened. Our team has been notified and is
                working on a fix.
              </p>
            </div>

            <div className="mt-8 space-y-4">
              <button
                type="button"
                onClick={() => window.location.reload()}
                className="group relative flex w-full justify-center rounded-md border border-transparent bg-[#A66B32] px-4 py-2 text-sm font-medium text-white hover:bg-[#241610] focus:outline-none focus:ring-2 focus:ring-[#A66B32]/30 focus:ring-offset-2"
              >
                Reload Page
              </button>

              <button
                type="button"
                onClick={() => (window.location.href = '/')}
                className="group relative flex w-full justify-center rounded-md border border-gray-300 bg-white px-4 py-2 text-sm font-medium text-gray-700 hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-[#A66B32]/30 focus:ring-offset-2"
              >
                Go to Homepage
              </button>
            </div>

            {import.meta.env.MODE === 'development' && this.state.error && (
              <div className="mt-6 rounded-md bg-red-50 p-4">
                <h3 className="text-sm font-medium text-red-800">
                  Error Details (Development Only)
                </h3>
                <pre className="mt-2 overflow-auto text-xs text-red-700">
                  {this.state.error.message}
                  {'\n\n'}
                  {this.state.error.stack}
                </pre>
              </div>
            )}
          </div>
        </div>
      );
    }

    return this.props.children;
  }
}

/**
 * Sentry Error Boundary (Alternative)
 *
 * Uses Sentry's built-in error boundary with additional features.
 * This is a drop-in replacement for the custom ErrorBoundary above.
 *
 * @example
 * <SentryErrorBoundary fallback={ErrorFallback} showDialog>
 *   <App />
 * </SentryErrorBoundary>
 */
export const SentryErrorBoundary = Sentry.ErrorBoundary;

/**
 * Error Fallback Component
 *
 * Custom fallback UI for Sentry Error Boundary.
 *
 * @example
 * <SentryErrorBoundary fallback={ErrorFallback}>
 *   <App />
 * </SentryErrorBoundary>
 */
export function ErrorFallback({ error, resetError }: { error: Error; resetError: () => void }) {
  return (
    <div className="flex min-h-screen items-center justify-center bg-gray-50 px-4 py-12 sm:px-6 lg:px-8">
      <div className="w-full max-w-md space-y-8">
        <div>
          <h2 className="mt-6 text-center text-3xl font-bold tracking-tight text-gray-900">
            Application Error
          </h2>
          <p className="mt-2 text-center text-sm text-gray-600">
            An unexpected error occurred. Our team has been notified.
          </p>
        </div>

        <div className="mt-8 space-y-4">
          <button
            type="button"
            onClick={resetError}
            className="group relative flex w-full justify-center rounded-md border border-transparent bg-[#A66B32] px-4 py-2 text-sm font-medium text-white hover:bg-[#241610] focus:outline-none focus:ring-2 focus:ring-[#A66B32]/30 focus:ring-offset-2"
          >
            Try Again
          </button>

          <button
            type="button"
            onClick={() => (window.location.href = '/')}
            className="group relative flex w-full justify-center rounded-md border border-gray-300 bg-white px-4 py-2 text-sm font-medium text-gray-700 hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-[#A66B32]/30 focus:ring-offset-2"
          >
            Go to Homepage
          </button>
        </div>

        {import.meta.env.MODE === 'development' && (
          <div className="mt-6 rounded-md bg-red-50 p-4">
            <h3 className="text-sm font-medium text-red-800">Error Details (Development Only)</h3>
            <pre className="mt-2 overflow-auto text-xs text-red-700">
              {error.message}
              {'\n\n'}
              {error.stack}
            </pre>
          </div>
        )}
      </div>
    </div>
  );
}
